import React, { useState, useEffect, useCallback } from 'react';
import './Analyse.css';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const MAANDEN = ['Jan','Feb','Mar','Apr','Mei','Jun','Jul','Aug','Sep','Okt','Nov','Dec'];

const Analyse = () => {
  const { terms } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periode, setPeriode] = useState('jaar');
  const [afdelingen, setAfdelingen] = useState([]);
  const [geselecteerdeAfdeling, setGeselecteerdeAfdeling] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = { periode };
      if (geselecteerdeAfdeling) params.afdeling_id = geselecteerdeAfdeling;
      const res = await api.get('/analyse/overzicht', { params });
      setData(res.data.data);
    } catch (error) {
      console.error('Fout bij laden analyse data:', error);
    } finally {
      setLoading(false);
    }
  }, [periode, geselecteerdeAfdeling]);

  useEffect(() => { api.get('/afdelingen').then(r => setAfdelingen(r.data.afdelingen || [])).catch(() => {}); }, []);
  useEffect(() => { loadData(); }, [loadData]);

  if (loading) return <div className="analyse-loading"><div className="spinner"></div><p>Analyse laden...</p></div>;
  if (!data) return <div className="analyse-loading"><p>Kon data niet laden</p></div>;

  const { werkorderStats, onderhoudStats, perMaand, monteurStats, teLaat, voortgang, topKlanten, afdelingen: afdelingStats } = data;
  const afgerondPct = voortgang.totaal > 0 ? Math.round((voortgang.afgerond / voortgang.totaal) * 100) : 0;
  const jaarPct = Math.round((voortgang.dag_van_jaar / voortgang.dagen_in_jaar) * 100);
  const opSchema = afgerondPct >= jaarPct;
  const maxPerMaand = Math.max(...MAANDEN.map((_, i) => { const m = perMaand.find(r => parseInt(r.maand_num) === i + 1); return m ? parseInt(m.aangemaakt) : 0; }), 1);
  const huidigJaar = new Date().getFullYear();

  return (
    <div className="analyse-container">
      <div className="print-header">
        <h1>WOMSYS - Analyse Rapport</h1>
        <p>Periode: {periode === 'jaar' ? `Jaar ${huidigJaar}` : periode} | Afgedrukt op: {new Date().toLocaleDateString('nl-NL')}</p>
      </div>

      <div className="analyse-toolbar no-print">
        <div className="analyse-title">
          <h1>📊 Analyse & Voortgang</h1>
          <span className="jaar-badge">{huidigJaar}</span>
        </div>
        <div className="analyse-filters">
          <select value={periode} onChange={e => setPeriode(e.target.value)}>
            <option value="week">Deze week</option>
            <option value="maand">Deze maand</option>
            <option value="kwartaal">Dit kwartaal</option>
            <option value="jaar">Dit jaar</option>
          </select>
          <select value={geselecteerdeAfdeling} onChange={e => setGeselecteerdeAfdeling(e.target.value)}>
            <option value="">Alle afdelingen</option>
            {afdelingen.map(a => <option key={a.id} value={a.id}>{a.naam}</option>)}
          </select>
          <button className="print-btn" onClick={() => window.location.href = '/storing-analyse'} style={{marginRight: '8px'}}>🔧 Storing-analyse</button>
          <button className="print-btn" onClick={() => window.open('/storingen-dashboard.html', '_blank')} style={{marginRight: '8px'}}>📋 Storingen Dashboard</button>
          <button className="print-btn" onClick={() => window.print()}>🖨️ Afdrukken</button>
        </div>
      </div>

      <div className={`voortgang-banner ${opSchema ? 'op-schema' : 'achter'}`}>
        <div className="voortgang-tekst">
          <span className="voortgang-icon">{opSchema ? '✅' : '⚠️'}</span>
          <div>
            <strong>{opSchema ? 'Op schema!' : 'Lichte achterstand'}</strong>
            <p>{afgerondPct}% van werkorders afgerond — jaar is {jaarPct}% voorbij</p>
          </div>
        </div>
        <div className="voortgang-balken">
          <div className="balk-rij"><span>Werkorders afgerond</span><div className="progress-bar"><div className="progress-fill groen" style={{width:`${afgerondPct}%`}}></div></div><span>{afgerondPct}%</span></div>
          <div className="balk-rij"><span>Jaar verstreken</span><div className="progress-bar"><div className="progress-fill blauw" style={{width:`${jaarPct}%`}}></div></div><span>{jaarPct}%</span></div>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-kaart blauw"><div className="stat-getal">{werkorderStats.totaal}</div><div className="stat-label">Totaal werkorders</div></div>
        <div className="stat-kaart groen"><div className="stat-getal">{werkorderStats.afgerond}</div><div className="stat-label">Afgerond</div></div>
        <div className="stat-kaart oranje"><div className="stat-getal">{parseInt(werkorderStats.open||0)+parseInt(werkorderStats.in_behandeling||0)}</div><div className="stat-label">Nog open</div></div>
        <div className="stat-kaart rood"><div className="stat-getal">{werkorderStats.restwerk||0}</div><div className="stat-label">Restwerk</div></div>
        <div className="stat-kaart grijs"><div className="stat-getal">{werkorderStats.gem_doorlooptijd_uren||'—'}</div><div className="stat-label">Gem. doorlooptijd (uur)</div></div>
      </div>
      {onderhoudStats && <div className="stats-grid" style={{marginTop:"12px"}}>
        <div className="stat-kaart" style={{background:'#eff6ff', color:'#1e40af'}}><div className="stat-getal">{onderhoudStats.onderhoud_totaal||0}</div><div className="stat-label">🔧 Onderhoud totaal</div></div>
        <div className="stat-kaart" style={{background:'#ecfdf5', color:'#065f46'}}><div className="stat-getal">{onderhoudStats.onderhoud_afgerond||0}</div><div className="stat-label">🔧 Onderhoud afgerond</div></div>
        <div className="stat-kaart" style={{background:'#fff7ed', color:'#9a3412'}}><div className="stat-getal">{onderhoudStats.onderhoud_open||0}</div><div className="stat-label">🔧 Onderhoud open</div></div>
        <div className="stat-kaart" style={{background:'#eff6ff', color:'#1e40af'}}><div className="stat-getal">{onderhoudStats.regulier_totaal||0}</div><div className="stat-label">📋 Overige totaal</div></div>
        <div className="stat-kaart" style={{background:'#ecfdf5', color:'#065f46'}}><div className="stat-getal">{onderhoudStats.regulier_afgerond||0}</div><div className="stat-label">📋 Overige afgerond</div></div>
        <div className="stat-kaart" style={{background:'#fff7ed', color:'#9a3412'}}><div className="stat-getal">{onderhoudStats.regulier_open||0}</div><div className="stat-label">📋 Overige open</div></div>
      </div>}

      <div className="twee-kolommen">
        <div className="kaart">
          <h2>📅 Werkorders per maand ({huidigJaar})</h2>
          <div className="staaf-grafiek">
            {MAANDEN.map((maand, i) => {
              const md = perMaand.find(r => parseInt(r.maand_num) === i + 1);
              const aangemaakt = md ? parseInt(md.aangemaakt) : 0;
              const afgerond = md ? parseInt(md.afgerond) : 0;
              const isHuidig = i + 1 === new Date().getMonth() + 1;
              return (
                <div key={maand} className={`staaf-kolom ${isHuidig ? 'huidig' : ''}`}>
                  <div className="staaf-wrapper">
                    <div className="staaf blauw" style={{height:`${Math.round((aangemaakt/maxPerMaand)*100)}%`}} title={`Aangemaakt: ${aangemaakt}`}></div>
                    <div className="staaf groen overlay" style={{height:`${Math.round((afgerond/maxPerMaand)*100)}%`}} title={`Afgerond: ${afgerond}`}></div>
                  </div>
                  <span className="staaf-label">{maand}</span>
                  {aangemaakt > 0 && <span className="staaf-getal">{aangemaakt}</span>}
                </div>
              );
            })}
          </div>
          <div className="grafiek-legenda">
            <span><span className="dot blauw"></span> Aangemaakt</span>
            <span><span className="dot groen"></span> Afgerond</span>
          </div>
        </div>

        <div className="kaart">
          <h2>🔴 Aandachtspunten</h2>
          {teLaat.length === 0
            ? <div className="geen-items">✅ Geen openstaande werkorders ouder dan 7 dagen</div>
            : <div className="aandacht-lijst">{teLaat.map(w => (
                <div key={w.id} className={`aandacht-item ${w.dagen_open > 30 ? 'kritiek' : 'waarschuwing'}`}>
                  <div className="aandacht-info"><strong>{w.titel}</strong><span>{Math.round(w.dagen_open)} dagen open</span></div>
                  <span>{w.dagen_open > 30 ? '🔴' : '🟡'}</span>
                </div>
              ))}</div>
          }
        </div>
      </div>

      <div className="kaart">
        <h2>👷 Monteurs prestaties</h2>
        {monteurStats.length === 0 ? <div className="geen-items">Geen monteur data beschikbaar</div> : (
          <table className="analyse-tabel">
            <thead><tr><th>Monteur</th><th>Afgerond</th><th>Uren gewerkt</th><th>Restwerk</th><th>Score</th></tr></thead>
            <tbody>
              {monteurStats.map(m => {
                const score = m.werkorders_afgerond > 0 ? Math.max(0, Math.round(100 - (m.restwerk_orders / m.werkorders_afgerond * 100))) : 0;
                return (
                  <tr key={m.id}>
                    <td><strong>{m.naam}</strong></td>
                    <td>{m.werkorders_afgerond}</td>
                    <td>{parseFloat(m.totaal_uren||0).toFixed(1)}</td>
                    <td>{m.restwerk_orders}</td>
                    <td><div className="score-balk"><div className={`score-fill ${score>=80?'groen':score>=60?'oranje':'rood'}`} style={{width:`${score}%`}}></div></div><span className="score-getal">{score}%</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="twee-kolommen">
        <div className="kaart">
          <h2>🏢 Top klanten</h2>
          {topKlanten.length === 0 ? <div className="geen-items">Geen klanten data</div> : (
            <table className="analyse-tabel">
              <thead><tr><th>{terms?.klant_singular || "Klant"}</th><th>Werkorders</th><th>Open</th></tr></thead>
              <tbody>{topKlanten.map((k,i) => <tr key={i}><td>{k.naam}</td><td>{k.werkorders}</td><td><span className={`badge ${k.openstaand>0?'oranje':'groen'}`}>{k.openstaand}</span></td></tr>)}</tbody>
            </table>
          )}
        </div>
        <div className="kaart">
          <h2>🏭 Per afdeling</h2>
          {afdelingStats.length === 0 ? <div className="geen-items">Geen afdeling data</div> : (
            <table className="analyse-tabel">
              <thead><tr><th>Afdeling</th><th>Totaal</th><th>Afgerond</th><th>Open</th></tr></thead>
              <tbody>{afdelingStats.map((a,i) => <tr key={i}><td>{a.naam}</td><td>{a.totaal}</td><td><span className="badge groen">{a.afgerond}</span></td><td><span className={`badge ${a.open>0?'oranje':'groen'}`}>{a.open}</span></td></tr>)}</tbody>
            </table>
          )}
        </div>
      </div>

      <div className="print-footer">
        <p>WOMSYS Analyse Rapport — Gegenereerd op {new Date().toLocaleDateString('nl-NL')} om {new Date().toLocaleTimeString('nl-NL')}</p>
      </div>
    </div>
  );
};

export default Analyse;
